import Format from 'app/src/data/Format';
import CCGateway from 'app/src/data/models/CCGateway';
import RetailModel from 'app/src/data/models/RetailModel';
import URLHelper from 'app/src/data/models/utils/URLHelper';

// The constant values are must match what is returned via the API
const _GatewayType = {
  BARCLAYCARD: 'barclaycard',
  BNP_PARIBAS: 'bnp-paribas',
  CAYAN: 'cayan',
  CONCARDIS: 'concardis',
  ELEMENT_EXPRESS: 'element-express',
  FIRST_DATA: 'first-data',
  IZETTLE: 'izettle',
  LIGHTSPEED_PAYMENTS: 'lightspeed-payments',
  MERCURY_CANADA: 'mercury-canada',
  MERCURY_EMV: 'mercury-emv',
  MERCURY_PAYMENT_SYSTEMS: 'mercury-payment-systems',
  TYRO: 'tyro',
  VANTIV_CANADA: 'vantiv-canada',
  VANTIV_TRIPOS: 'vantiv-tripos',
  VANTIV_US: 'vantiv-us',
  WORLDLINE: 'worldline',
};

export default class GatewayConfig extends RetailModel {
  /**
   * Exports the GatewayType
   */
  static get GatewayType() {
    return _GatewayType;
  }

  static PLATFORM_VERSIONS = {
    v1: '1',
    v3: '3',
  };

  // Server uses the gatewayConfigID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'gatewayConfigID';
  }

  get defaults() {
    return {
      name: '',
      gatewayID: '',
    };
  }

  get retailResponseName() {
    return 'GatewayConfig';
  }

  /**
   * Determines if the gateway allows negative payments
   * @returns {*|Boolean}
   */
  get allowNegativePayments() {
    return Format.parseBoolean(this.get('allowNegativePayments'), false);
  }

  /**
   * Return the url for the GatewayConfig endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/GatewayConfig');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    // trim out wonky stuff from the response which is present if we fetch a single model
    // but not if it's coming from a collection
    let gatewayConfig = super.parse(response);

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    if (
      gatewayConfig.gatewayConfigID &&
      gatewayConfig.gatewayConfigID.match &&
      !gatewayConfig.gatewayConfigID.match(/-/)
    ) {
      // the ID may be a UUID for certain gateway configurations (e.g. clients migrated to the payment service)
      gatewayConfig.gatewayConfigID = Format.parseInt(gatewayConfig.gatewayConfigID);
    }

    return gatewayConfig;
  }

  /**
   * Delegates to the CCGateway endpoint to initiate a Cayan payment for now since
   * that has not yet been ported to the payment service.
   *
   * @param amount {Decimal}  The amount to be transacted.
   * @param saleID {Number}   The ID of the sale for the transaction.
   * @param key {String}      The terminal key
   * @param tipAmount {Decimal}  The tipAmount to be transacted.
   *
   * @returns {jQuery.Promise}    Resolves with the Cayan transaction keys.
   */
  createGeniusTransaction(amount, saleID, key, tipAmount = null) {
    let ccGateway = new CCGateway({
      ccGatewayID: this.id,
    });

    return ccGateway.createGeniusTransaction(amount, saleID, key, tipAmount);
  }
}
